import { BaseModel } from '../base.model';
import { AGMediaKeys } from '../utils/ag-media.model';
import {
  SUPPORTING_TABLE_IDS,
  SUPPORTING_TABLE_IMPORT_STATUSES,
  SupportingTableId,
  SupportingTableImportStatus,
} from './supporting-table-shared.model';

export {
  SUPPORTING_TABLE_IDS,
  SUPPORTING_TABLE_IMPORT_STATUSES,
  SupportingTableId,
  SupportingTableImportStatus,
} from './supporting-table-shared.model';

export const REPORTING_SUPPORTING_TABLES_COLLECTION = 'supporting-tables';
export const REPORTING_SUPPORTING_TABLE_IMPORTS_COLLECTION = 'supporting-table-imports';
export const REPORTING_SUPPORTING_TABLE_SIGNALS_COLLECTION = 'supporting-tables';

const SUPPORTING_TABLE_ID_SET = new Set<SupportingTableId>(SUPPORTING_TABLE_IDS);

export enum SupportingTableChangeOperation {
  Insert = 'insert',
  Update = 'update',
  Delete = 'delete',
}

export const SUPPORTING_TABLE_CHANGE_OPERATIONS = Object.values(
  SupportingTableChangeOperation,
) as SupportingTableChangeOperation[];

export enum SupportingTableSignalKeys {
  supportingTableId = 'supportingTableId',
  version = 'version',
  updatedAt = 'updatedAt',
  lastOperation = 'lastOperation',
  lastRowNumber = 'lastRowNumber',
  lastOutboxEventId = 'lastOutboxEventId',
  source = 'source',
}

export interface SupportingTableSignal extends BaseModel {
  [SupportingTableSignalKeys.supportingTableId]: SupportingTableId;
  [SupportingTableSignalKeys.version]: number;
  [SupportingTableSignalKeys.updatedAt]: Date | string | null;
  [SupportingTableSignalKeys.lastOperation]?: SupportingTableChangeOperation | null;
  [SupportingTableSignalKeys.lastRowNumber]?: number | null;
  [SupportingTableSignalKeys.lastOutboxEventId]?: number | null;
  [SupportingTableSignalKeys.source]?: string | null;
}

export type SupportingTableGridColumnDataType = 'string' | 'number' | 'date' | 'datetime' | 'boolean';

export interface SupportingTableGridColumnDefinition {
  dataField: string;
  caption: string;
  dataType: SupportingTableGridColumnDataType;
  required?: boolean;
  allowEditing?: boolean;
  allowFiltering?: boolean;
  allowSorting?: boolean;
  allowGrouping?: boolean;
  width?: number;
}

export interface SupportingTableSourceColumn {
  header: string;
  dataField: string;
  dataType: SupportingTableGridColumnDataType;
}

export interface SupportingTableDefinition {
  id: SupportingTableId;
  title: string;
  description: string;
  instructions: string[];
  sqlTableName: string;
  sourceFileNamePattern: string;
  acceptedExtensions: string[];
  gridColumns: SupportingTableGridColumnDefinition[];
  defaultSort?: Array<{
    selector: string;
    desc?: boolean;
  }>;
}

export const SUPPORTING_TABLE_DEFINITIONS: SupportingTableDefinition[] = [
  {
    id: SupportingTableId.AgencyCodeNameMapping,
    title: 'Agency Code Mapping',
    description: 'Agency number, agency name, and carrier code mapping used during reporting normalization.',
    instructions: [
      'Upload the latest agency mapping Excel file.',
      'Use this table when reporting normalization depends on agency number and carrier code mappings.',
    ],
    sqlTableName: 'agcy_code_name_mod',
    sourceFileNamePattern: 'AGCY CODE - NAME MOD',
    acceptedExtensions: ['.xlsx', '.xls'],
    gridColumns: [
      { dataField: 'agency_number', caption: 'Agency Number', dataType: 'string', required: true },
      { dataField: 'agency_name', caption: 'Agency Name', dataType: 'string' },
      { dataField: 'carrier_code', caption: 'Carrier Code', dataType: 'string' },
      /*
       * The code reconciliation reports this agency under, maintained here rather than
       * uploaded (TASKID-3602).
       *
       * `carrier_code` replaces the recruiter on the master row for Access parity, and the
       * master tables store it that way, so the code the agency's own carrier file stamped
       * is gone by the time reconciliation reads it. Set this to that original code and the
       * reconciliation rebuild reports the agency's business under it instead; leave it
       * blank and nothing changes. The upload contract stays ['AGENCY #','Agency Name',
       * 'Carrier Code'] -- see SUPPORTING_TABLE_SOURCE_HEADERS -- and this is carried
       * forward when a new import replaces the rows.
       */
      { dataField: 'reconciliation_carrier_code', caption: 'Reconciliation Code', dataType: 'string' },
    ],
    defaultSort: [{ selector: 'agency_number' }],
  },
  {
    id: SupportingTableId.AgentSuppressions,
    title: 'Agent Suppressions',
    description: 'Suppression rows that exclude selected agents from downstream reporting logic.',
    instructions: [
      'Upload the current suppression list from the business source.',
      'Replacing the file should rebuild the active SQL snapshot for suppressed agents.',
    ],
    sqlTableName: 'agent_suppressions',
    sourceFileNamePattern: 'AGENT SUPPRESSION TABLE',
    acceptedExtensions: ['.xlsx', '.xls'],
    gridColumns: [
      { dataField: 'mga_name', caption: 'MGA Name', dataType: 'string' },
      { dataField: 'agent_name', caption: 'Agent Name', dataType: 'string', required: true },
      { dataField: 'agent_suppression_ind', caption: 'Suppression Indicator', dataType: 'string', required: true },
      { dataField: 'suppress_detail', caption: 'Suppress Detail', dataType: 'string' },
    ],
    defaultSort: [{ selector: 'agent_name' }],
  },
  {
    id: SupportingTableId.AgencyNtp,
    title: 'Agency NTP',
    description: 'Agency NTP lookup used by reporting transforms and carrier-specific adjustments.',
    instructions: [
      'Upload the NTP Excel source currently used by reporting operations.',
      'Keep the newest approved version here before rerunning dependent imports.',
    ],
    sqlTableName: 'agency_ntp',
    sourceFileNamePattern: 'NTP Table',
    acceptedExtensions: ['.xlsx', '.xls'],
    gridColumns: [
      { dataField: 'agency_name', caption: 'Agency Name', dataType: 'string', required: true },
      { dataField: 'ntp_ind', caption: 'NTP Indicator', dataType: 'string', required: true },
      { dataField: 'ntp_name', caption: 'NTP Name', dataType: 'string' },
    ],
    defaultSort: [{ selector: 'agency_name' }],
  },
  {
    id: SupportingTableId.NtpMgaTerminatedAgents,
    title: 'NTP MGA UPD (Terminated Agents)',
    description: 'Agent-level overrides applied to unified Paid and Submit outputs by matching Agent Name.',
    instructions: [
      'Upload "NTP MGA UPD TABLE (TERMINATED AGENTS).xlsx".',
      'Applies to all carrier feeds that produce Paid rows (Foresters, National Life, AIMCOR, GPM, Corebridge, Ameritas, ANICO).',
      'Applies to all carrier feeds that produce Submit rows (Foresters, National Life, AIMCOR, GPM, Corebridge, Ameritas, ANICO).',
      'Matching key is Agent Name. Overrides map agency_name -> Paid.Agency Name / Submit.MGAName and notes -> Paid.ADJ NOTES / Submit.Adj Note.',
    ],
    sqlTableName: 'ntp_mga_terminated_agents',
    sourceFileNamePattern: 'NTP MGA UPD TABLE (TERMINATED AGENTS)',
    acceptedExtensions: ['.xlsx', '.xls'],
    gridColumns: [
      { dataField: 'mga_name', caption: 'MGA Name', dataType: 'string', required: true },
      { dataField: 'agent_name', caption: 'Agent Name', dataType: 'string', required: true },
      { dataField: 'agency_name', caption: 'Agency Name', dataType: 'string', required: true },
      { dataField: 'ntp_name', caption: 'NTP Name', dataType: 'string', required: true },
      { dataField: 'notes', caption: 'Notes', dataType: 'string' },
    ],
    defaultSort: [{ selector: 'agent_name' }],
  },
  {
    id: SupportingTableId.AgencyRmd,
    title: 'Agency RMD',
    description: 'Agency RMD lookup used by historical reporting rules and downstream enrichments.',
    instructions: [
      'Upload the latest RMD lookup file for agency-level enrichments.',
      'Use replacement when business sends a full refreshed snapshot.',
    ],
    sqlTableName: 'agency_rmd',
    sourceFileNamePattern: 'RMD TABLE',
    acceptedExtensions: ['.xlsx', '.xls'],
    gridColumns: [
      { dataField: 'agency_name', caption: 'Agency Name', dataType: 'string', required: true },
      { dataField: 'rmd_1', caption: 'RMD 1', dataType: 'string', required: true },
      { dataField: 'rmd_2', caption: 'RMD 2', dataType: 'string' },
    ],
    defaultSort: [{ selector: 'agency_name' }],
  },
  {
    id: SupportingTableId.ForestersTable,
    title: 'Foresters Table',
    description: 'Foresters agent-name mapping used to align source agent naming with reporting naming.',
    instructions: [
      'Upload "FORESTERS TABLE.xlsx".',
      'Used for Foresters Submit/Paid agent-name normalization when file agent names differ from reporting agent names.',
    ],
    sqlTableName: 'foresters_table',
    sourceFileNamePattern: 'FORESTERS TABLE',
    acceptedExtensions: ['.xlsx', '.xls'],
    gridColumns: [
      { dataField: 'mga_name', caption: 'MGA', dataType: 'string' },
      { dataField: 'file_agent_name', caption: 'File Agent Name', dataType: 'string', required: true },
      { dataField: 'rpt_agent_name', caption: 'Report Agent Name', dataType: 'string', required: true },
    ],
    defaultSort: [{ selector: 'rpt_agent_name' }],
  },
  {
    id: SupportingTableId.MgaTable,
    title: 'MGA Table',
    description: 'MGA normalization mapping used to replace file-side MGA names with reporting MGA names in Paid and Submit.',
    instructions: [
      'Upload "MGA.xlsx".',
      'Used to normalize Paid.Agency Name and Submit.MGAName via FILE MGA -> RPT MGA mapping.',
    ],
    sqlTableName: 'mga',
    sourceFileNamePattern: 'MGA',
    acceptedExtensions: ['.xlsx', '.xls'],
    gridColumns: [
      { dataField: 'mga_name', caption: 'MGA', dataType: 'string' },
      { dataField: 'file_mga', caption: 'File MGA', dataType: 'string', required: true },
      { dataField: 'rpt_mga', caption: 'Report MGA', dataType: 'string', required: true },
    ],
    defaultSort: [{ selector: 'file_mga' }],
  },
  {
    id: SupportingTableId.NlgProdNameTable,
    title: 'NLG Prod Name Table',
    description: 'NLG writing-number to production-agent mapping used to normalize Paid and Submit agent names.',
    instructions: [
      'Upload "Production Name - Writing # table.xlsx".',
      'Used by Access parity updates: Paid.Agent Name and Submit.AgentName are replaced by agent_name when NLG # matches agent number.',
    ],
    sqlTableName: 'nlg_prod_name_table',
    sourceFileNamePattern: 'Production Name - Writing # table',
    acceptedExtensions: ['.xlsx', '.xls'],
    gridColumns: [
      { dataField: 'mga_name', caption: 'MGA Name', dataType: 'string' },
      { dataField: 'nlg_number', caption: 'NLG #', dataType: 'string', required: true },
      { dataField: 'agent_id', caption: 'Agent ID', dataType: 'string' },
      { dataField: 'agent_name', caption: 'Agent Name', dataType: 'string', required: true },
    ],
    defaultSort: [{ selector: 'nlg_number' }],
  },
  {
    id: SupportingTableId.AgentMgaCorrections,
    title: 'AGT MGA CORRECTIONS',
    description: 'Agent-name to MGA override mapping used by Access updates for Paid and Submit normalization.',
    instructions: [
      'Upload "AGENT-MGA CORRECTIONS.xlsx".',
      'Used by Access parity updates: Paid.Agency Name and Submit.MGAName are replaced by MGA NAME when FILE AGENT NAME matches.',
    ],
    sqlTableName: 'agent_mga_corrections',
    sourceFileNamePattern: 'AGENT-MGA CORRECTIONS',
    acceptedExtensions: ['.xlsx', '.xls'],
    gridColumns: [
      { dataField: 'reported_mga', caption: 'Reported MGA', dataType: 'string' },
      { dataField: 'file_agent_name', caption: 'File Agent Name', dataType: 'string', required: true },
      { dataField: 'mga_name', caption: 'MGA Name', dataType: 'string', required: true },
    ],
    defaultSort: [{ selector: 'file_agent_name' }],
  },
  {
    id: SupportingTableId.CarriersTable,
    title: 'Carriers Table',
    description: 'Recruiter-to-carrier mapping used by Access updates for Paid and Submit carrier name normalization.',
    instructions: [
      'Upload "Carriers mm.dd.yy.xlsx".',
      'Used by Access parity updates: Paid.carrier_name and Submit.Carrier are replaced by carrier_name when recruiter matches.',
    ],
    sqlTableName: 'carriers',
    sourceFileNamePattern: 'Carriers',
    acceptedExtensions: ['.xlsx', '.xls'],
    gridColumns: [
      { dataField: 'recruiter', caption: 'Recruiter', dataType: 'string', required: true },
      { dataField: 'carrier_name', caption: 'Carrier Name', dataType: 'string', required: true },
      /*
       * Reconciliation line attributes (TASKID-3602), maintained here rather than uploaded.
       *
       * The upload contract stays ['recruiter','carrier_name'] -- see
       * SUPPORTING_TABLE_SOURCE_HEADERS -- so the legacy third-party round-trip is
       * unchanged. These are edited in the grid and carried forward when a new import
       * replaces the rows.
       *
       * `line_label` is seeded from `carrier_name` on import rather than left blank: it is
       * an editable default the business adjusts, and an empty cell gives them nothing to
       * edit from. `show_on_paid` / `show_on_submitted` are deliberately NOT here -- they
       * are still carried across imports, just not maintained in this grid.
       */
      { dataField: 'line_label', caption: 'Line Label', dataType: 'string' },
      { dataField: 'sort_order', caption: 'Sort Order', dataType: 'number' },
    ],
    defaultSort: [{ selector: 'recruiter' }],
  },
  {
    id: SupportingTableId.GpmEquisMgaUpdate,
    title: 'GPM - EQUIS MGA UPDATE',
    description: 'MGA code-to-name lookup used by Access updates for GPM/TAG agency normalization in Paid and Submit.',
    instructions: [
      'Upload "GPM - Equis MGA update.xlsx".',
      'Used by Access parity updates: Paid.Agency Name and Submit.MGAName are replaced by MGA Name when MGA Code matches.',
    ],
    sqlTableName: 'gpm_equis_mga_update',
    sourceFileNamePattern: 'GPM - Equis MGA update',
    acceptedExtensions: ['.xlsx', '.xls'],
    gridColumns: [
      { dataField: 'mga_code', caption: 'MGA Code', dataType: 'string', required: true },
      { dataField: 'mga_name', caption: 'MGA Name', dataType: 'string', required: true },
    ],
    defaultSort: [{ selector: 'mga_code' }],
  },
];

const SUPPORTING_TABLE_DEFINITION_MAP = new Map<SupportingTableId, SupportingTableDefinition>(
  SUPPORTING_TABLE_DEFINITIONS.map(definition => [definition.id, definition]),
);

/**
 * Header row of the Excel file every supporting table is imported from, in source column order.
 * Import handlers validate the uploaded worksheet against these exact strings, and the CRM export
 * writes them back out, so a table can round-trip between the app and the legacy third-party tools.
 * Each entry is positionally paired with the table's `gridColumns`, so both lists must stay aligned.
 */
export const SUPPORTING_TABLE_SOURCE_HEADERS = {
  [SupportingTableId.AgencyCodeNameMapping]: ['AGENCY #', 'Agency Name', 'Carrier Code'],
  [SupportingTableId.AgentSuppressions]: ['MGA Name', 'Agent Name', 'AGENT SUPPRESSION IND', 'Suppress Detail'],
  [SupportingTableId.AgencyNtp]: ['Agency Name', 'NTP IND', 'NTP NAME'],
  [SupportingTableId.NtpMgaTerminatedAgents]: ['MGA NAME', 'AGENT NAME', 'AGENCY NAME', 'NTP NAME', 'NOTES'],
  [SupportingTableId.AgencyRmd]: ['Agency Name', 'rmd_1', 'rmd_2'],
  [SupportingTableId.ForestersTable]: ['MGA', 'FILE AGENT NAME', 'RPT AGENT NAME'],
  [SupportingTableId.MgaTable]: ['MGA', 'FILE MGA', 'RPT MGA'],
  [SupportingTableId.NlgProdNameTable]: ['mga_name', 'NLG #', 'agent_id', 'agent_name'],
  [SupportingTableId.AgentMgaCorrections]: ['REPORTED MGA', 'FILE AGENT NAME', 'MGA NAME'],
  [SupportingTableId.CarriersTable]: ['recruiter', 'carrier_name'],
  [SupportingTableId.GpmEquisMgaUpdate]: ['MGA Code', 'MGA Name'],
} as const satisfies Record<SupportingTableId, readonly string[]>;

export enum SupportingTableRecordKeys {
  supportingTableId = 'supportingTableId',
  uploadActionId = 'uploadActionId',
  processingStatus = 'processingStatus',
  processingErrorMessage = 'processingErrorMessage',
  lastHandledUploadActionId = 'lastHandledUploadActionId',
  recordsTotal = 'recordsTotal',
  activeImportId = 'activeImportId',
  lastChangeAt = 'lastChangeAt',
  lastChangeType = 'lastChangeType',
  lastChangedByAgentDbId = 'lastChangedByAgentDbId',
  lastChangedByEmail = 'lastChangedByEmail',
  lastChangedByName = 'lastChangedByName',
}

/**
 * User-driven changes to a supporting table. Tracked separately from BaseModel's `updated_*` because
 * the processor and the write trigger bump `updated_date` on every status transition with no actor,
 * so those fields cannot answer "who last changed this table".
 */
export enum SupportingTableLastChangeType {
  Upload = 'upload',
  RowCreate = 'row-create',
  RowUpdate = 'row-update',
  RowDelete = 'row-delete',
}

export enum SupportingTableProcessingStatus {
  Uploaded = 'uploaded',
  Queued = 'queued',
  Processing = 'processing',
  Completed = 'completed',
  Failed = 'failed',
}

export enum SupportingTableImportKeys {
  id = 'id',
  tableId = 'table_id',
  uploadActionId = 'upload_action_id',
  sourceFileName = 'source_file_name',
  sourceFilePath = 'source_file_path',
  sourceContentType = 'source_content_type',
  sourceFileSize = 'source_file_size',
  status = 'status',
  processingErrorMessage = 'processing_error_message',
  recordsTotal = 'records_total',
  effectiveFrom = 'effective_from',
  effectiveTo = 'effective_to',
  completedAt = 'completed_at',
  createdAt = 'created_at',
  updatedAt = 'updated_at',
}

export enum AgencyCodeNameMappingRowKeys {
  rowNumber = 'row_number',
  agencyNumber = 'agency_number',
  agencyName = 'agency_name',
  carrierCode = 'carrier_code',
  reconciliationCarrierCode = 'reconciliation_carrier_code',
  createdAt = 'created_at',
}

export enum AgentSuppressionRowKeys {
  rowNumber = 'row_number',
  mgaName = 'mga_name',
  agentName = 'agent_name',
  agentSuppressionInd = 'agent_suppression_ind',
  suppressDetail = 'suppress_detail',
  createdAt = 'created_at',
}

export enum AgencyNtpRowKeys {
  rowNumber = 'row_number',
  agencyName = 'agency_name',
  ntpInd = 'ntp_ind',
  ntpName = 'ntp_name',
  createdAt = 'created_at',
}

export enum NtpMgaTerminatedAgentsRowKeys {
  rowNumber = 'row_number',
  mgaName = 'mga_name',
  agentName = 'agent_name',
  agencyName = 'agency_name',
  ntpName = 'ntp_name',
  notes = 'notes',
  createdAt = 'created_at',
}

export enum AgencyRmdRowKeys {
  rowNumber = 'row_number',
  agencyName = 'agency_name',
  rmd1 = 'rmd_1',
  rmd2 = 'rmd_2',
  createdAt = 'created_at',
}

export enum ForestersTableRowKeys {
  rowNumber = 'row_number',
  mgaName = 'mga_name',
  fileAgentName = 'file_agent_name',
  rptAgentName = 'rpt_agent_name',
  enabled = 'enabled',
  createdAt = 'created_at',
}

export enum MgaTableRowKeys {
  rowNumber = 'row_number',
  mgaName = 'mga_name',
  fileMga = 'file_mga',
  rptMga = 'rpt_mga',
  enabled = 'enabled',
  createdAt = 'created_at',
}

export enum NlgProdNameTableRowKeys {
  rowNumber = 'row_number',
  mgaName = 'mga_name',
  nlgNumber = 'nlg_number',
  agentId = 'agent_id',
  agentName = 'agent_name',
  enabled = 'enabled',
  createdAt = 'created_at',
}

export enum AgentMgaCorrectionsRowKeys {
  rowNumber = 'row_number',
  reportedMga = 'reported_mga',
  fileAgentName = 'file_agent_name',
  mgaName = 'mga_name',
  enabled = 'enabled',
  createdAt = 'created_at',
}

export enum CarriersTableRowKeys {
  rowNumber = 'row_number',
  recruiter = 'recruiter',
  carrierName = 'carrier_name',
  /** Reconciliation line this code reports under; falls back to carrier_name when unset. */
  lineLabel = 'line_label',
  sortOrder = 'sort_order',
  showOnPaid = 'show_on_paid',
  showOnSubmitted = 'show_on_submitted',
  createdAt = 'created_at',
}

export enum GpmEquisMgaUpdateRowKeys {
  rowNumber = 'row_number',
  mgaCode = 'mga_code',
  mgaName = 'mga_name',
  createdAt = 'created_at',
}

export interface SupportingTableRecord extends BaseModel {
  [AGMediaKeys.fileName]: string;
  [AGMediaKeys.fileSize]: number;
  [AGMediaKeys.wasabiPath]: string;
  [AGMediaKeys.contentType]: string;
  [SupportingTableRecordKeys.supportingTableId]: SupportingTableId;
  [SupportingTableRecordKeys.uploadActionId]: string;
  [SupportingTableRecordKeys.processingStatus]: SupportingTableProcessingStatus;
  [SupportingTableRecordKeys.processingErrorMessage]?: string | null;
  [SupportingTableRecordKeys.lastHandledUploadActionId]?: string | null;
  [SupportingTableRecordKeys.recordsTotal]?: number | null;
  [SupportingTableRecordKeys.activeImportId]?: string | null;
  [SupportingTableRecordKeys.lastChangeAt]?: Date | string | null;
  [SupportingTableRecordKeys.lastChangeType]?: SupportingTableLastChangeType | null;
  [SupportingTableRecordKeys.lastChangedByAgentDbId]?: string | null;
  [SupportingTableRecordKeys.lastChangedByEmail]?: string | null;
  [SupportingTableRecordKeys.lastChangedByName]?: string | null;
}

export interface SupportingTableImport {
  [SupportingTableImportKeys.id]: string;
  [SupportingTableImportKeys.tableId]: SupportingTableId;
  [SupportingTableImportKeys.uploadActionId]: string;
  [SupportingTableImportKeys.sourceFileName]: string;
  [SupportingTableImportKeys.sourceFilePath]: string;
  [SupportingTableImportKeys.sourceContentType]?: string | null;
  [SupportingTableImportKeys.sourceFileSize]?: number | null;
  [SupportingTableImportKeys.status]: SupportingTableImportStatus;
  [SupportingTableImportKeys.processingErrorMessage]?: string | null;
  [SupportingTableImportKeys.recordsTotal]?: number | null;
  [SupportingTableImportKeys.effectiveFrom]?: Date | string | null;
  [SupportingTableImportKeys.effectiveTo]?: Date | string | null;
  [SupportingTableImportKeys.completedAt]?: Date | string | null;
  [SupportingTableImportKeys.createdAt]: Date | string;
  [SupportingTableImportKeys.updatedAt]: Date | string;
}

export interface AgencyCodeNameMappingRow {
  [AgencyCodeNameMappingRowKeys.rowNumber]: number;
  [AgencyCodeNameMappingRowKeys.agencyNumber]: string;
  [AgencyCodeNameMappingRowKeys.agencyName]?: string | null;
  [AgencyCodeNameMappingRowKeys.carrierCode]?: string | null;
  [AgencyCodeNameMappingRowKeys.reconciliationCarrierCode]?: string | null;
  [AgencyCodeNameMappingRowKeys.createdAt]: Date | string;
}

export interface AgentSuppressionRow {
  [AgentSuppressionRowKeys.rowNumber]: number;
  [AgentSuppressionRowKeys.mgaName]?: string | null;
  [AgentSuppressionRowKeys.agentName]: string;
  [AgentSuppressionRowKeys.agentSuppressionInd]: string;
  [AgentSuppressionRowKeys.suppressDetail]?: string | null;
  [AgentSuppressionRowKeys.createdAt]: Date | string;
}

export interface AgencyNtpRow {
  [AgencyNtpRowKeys.rowNumber]: number;
  [AgencyNtpRowKeys.agencyName]: string;
  [AgencyNtpRowKeys.ntpInd]: string;
  [AgencyNtpRowKeys.ntpName]?: string | null;
  [AgencyNtpRowKeys.createdAt]: Date | string;
}

export interface NtpMgaTerminatedAgentsRow {
  [NtpMgaTerminatedAgentsRowKeys.rowNumber]: number;
  [NtpMgaTerminatedAgentsRowKeys.mgaName]: string;
  [NtpMgaTerminatedAgentsRowKeys.agentName]: string;
  [NtpMgaTerminatedAgentsRowKeys.agencyName]: string;
  [NtpMgaTerminatedAgentsRowKeys.ntpName]: string;
  [NtpMgaTerminatedAgentsRowKeys.notes]?: string | null;
  [NtpMgaTerminatedAgentsRowKeys.createdAt]: Date | string;
}

export interface AgencyRmdRow {
  [AgencyRmdRowKeys.rowNumber]: number;
  [AgencyRmdRowKeys.agencyName]: string;
  [AgencyRmdRowKeys.rmd1]: string;
  [AgencyRmdRowKeys.rmd2]?: string | null;
  [AgencyRmdRowKeys.createdAt]: Date | string;
}

export interface ForestersTableRow {
  [ForestersTableRowKeys.rowNumber]: number;
  [ForestersTableRowKeys.mgaName]: string;
  [ForestersTableRowKeys.fileAgentName]: string;
  [ForestersTableRowKeys.rptAgentName]: string;
  [ForestersTableRowKeys.enabled]?: boolean;
  [ForestersTableRowKeys.createdAt]: Date | string;
}

export interface MgaTableRow {
  [MgaTableRowKeys.rowNumber]: number;
  [MgaTableRowKeys.mgaName]?: string | null;
  [MgaTableRowKeys.fileMga]: string;
  [MgaTableRowKeys.rptMga]: string;
  [MgaTableRowKeys.enabled]?: boolean;
  [MgaTableRowKeys.createdAt]: Date | string;
}

export interface NlgProdNameTableRow {
  [NlgProdNameTableRowKeys.rowNumber]: number;
  [NlgProdNameTableRowKeys.mgaName]?: string | null;
  [NlgProdNameTableRowKeys.nlgNumber]: string;
  [NlgProdNameTableRowKeys.agentId]?: string | null;
  [NlgProdNameTableRowKeys.agentName]: string;
  [NlgProdNameTableRowKeys.enabled]?: boolean;
  [NlgProdNameTableRowKeys.createdAt]: Date | string;
}

export interface AgentMgaCorrectionsRow {
  [AgentMgaCorrectionsRowKeys.rowNumber]: number;
  [AgentMgaCorrectionsRowKeys.reportedMga]?: string | null;
  [AgentMgaCorrectionsRowKeys.fileAgentName]: string;
  [AgentMgaCorrectionsRowKeys.mgaName]: string;
  [AgentMgaCorrectionsRowKeys.enabled]?: boolean;
  [AgentMgaCorrectionsRowKeys.createdAt]: Date | string;
}

export interface CarriersTableRow {
  [CarriersTableRowKeys.rowNumber]: number;
  [CarriersTableRowKeys.recruiter]: string;
  [CarriersTableRowKeys.carrierName]: string;
  [CarriersTableRowKeys.lineLabel]: string | null;
  [CarriersTableRowKeys.sortOrder]: number | null;
  [CarriersTableRowKeys.showOnPaid]: boolean;
  [CarriersTableRowKeys.showOnSubmitted]: boolean;
  [CarriersTableRowKeys.createdAt]: Date | string;
}

export interface GpmEquisMgaUpdateRow {
  [GpmEquisMgaUpdateRowKeys.rowNumber]: number;
  [GpmEquisMgaUpdateRowKeys.mgaCode]: string;
  [GpmEquisMgaUpdateRowKeys.mgaName]: string;
  [GpmEquisMgaUpdateRowKeys.createdAt]: Date | string;
}

export function isSupportingTableId(value: string): value is SupportingTableId {
  return SUPPORTING_TABLE_ID_SET.has(value as SupportingTableId);
}

export function getSupportingTableDefinition(tableId: SupportingTableId): SupportingTableDefinition {
  const definition = SUPPORTING_TABLE_DEFINITION_MAP.get(tableId);

  if (!definition) {
    throw new Error(`No supporting table definition is registered for "${tableId}"`);
  }

  return definition;
}

export function getSupportingTableSourceHeaders(tableId: SupportingTableId): readonly string[] {
  const headers = SUPPORTING_TABLE_SOURCE_HEADERS[tableId];

  if (!headers) {
    throw new Error(`No supporting table source headers are registered for "${tableId}"`);
  }

  return headers;
}

/** Source file columns of a supporting table: the imported header paired with the SQL column it feeds. */
export function getSupportingTableSourceColumns(tableId: SupportingTableId): SupportingTableSourceColumn[] {
  const gridColumns = getSupportingTableDefinition(tableId).gridColumns;
  const headers = getSupportingTableSourceHeaders(tableId);

  if (headers.length !== gridColumns.length) {
    throw new Error(
      `Supporting table "${tableId}" declares ${headers.length} source headers but ${gridColumns.length} columns`,
    );
  }

  return gridColumns.map((column, index) => ({
    header: headers[index],
    dataField: column.dataField,
    dataType: column.dataType,
  }));
}
